/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type {
  ProgressTile,
  ProgressTileLine,
} from '@sportninja/common/features/profile/stats/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getSeriesColor } from '@sportninja/common/features/profile/stats/utils/seriesColors'

import Text from 'src/components/ts-components/Text'

import DeltaBadge from './DeltaBadge'

interface ProgressTilesProps {
  tiles: ProgressTile[]
}

/** One charted stat inside a tile, marked with its line's colour. */
const TileLine = ({ line }: { line: ProgressTileLine }) => {
  'use memo'

  return (
    <div css={lineStyles}>
      <span
        aria-hidden='true'
        css={dotStyles(getSeriesColor(line.colorSlot, 'dark'))}
      />
      <Text variant='paragraphXSmall' weight='medium' color={colors.NEUTRAL_50}>
        {capitalizeWords(line.label)}
      </Text>
      <span css={lineSpacerStyles} />
      <Text variant='paragraphSmall' weight='bold' color={colors.WHITE}>
        {line.value}
      </Text>
      {line.delta ? <DeltaBadge delta={line.delta} /> : null}
      {line.caption ? (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_100}
        >
          {capitalizeWords(line.caption)}
        </Text>
      ) : null}
    </div>
  )
}

/**
 * Summary tiles above the trend line; one line per stat when several are
 * charted. A stat or games tile with a comparison shows its change against the
 * previous window beside the value.
 */
const ProgressTiles = ({ tiles }: ProgressTilesProps) => {
  'use memo'

  return (
    <div css={gridStyles}>
      {tiles.map((tile) => (
        <div key={tile.label} css={tileStyles}>
          <Text
            variant='paragraphXSmall'
            weight='medium'
            color={colors.NEUTRAL_50}
          >
            {capitalizeWords(tile.label)}
          </Text>
          {tile.lines ? (
            <div css={linesStyles}>
              {tile.lines.map((line) => (
                <TileLine key={line.colorSlot} line={line} />
              ))}
            </div>
          ) : (
            <div css={valueRowStyles}>
              <Text variant='heading5' weight='bold' color={colors.WHITE}>
                {tile.value}
              </Text>
              {tile.delta ? <DeltaBadge delta={tile.delta} /> : null}
            </div>
          )}
          {tile.caption ? (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_100}
            >
              {capitalizeWords(tile.caption)}
            </Text>
          ) : null}
        </div>
      ))}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const DOT_SIZE = 8

const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`

const tileStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${colors.SECONDARY_300};
`

const valueRowStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const linesStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
`

const lineStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const lineSpacerStyles = css`
  flex: 1;
`

const dotStyles = (color: string) => css`
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${color};
`

export default ProgressTiles
